import { useMemo } from 'react';

import { useGetSummaryGraphQuery } from '~domains/dashboard/entities/summary';
import type { TransactionsSummaryFilter } from '~domains/dashboard/entities/summary';

import { formatMoney } from '~shared/lib/format-money';
import { useIsDesktop } from '~shared/lib/responsive';
import { Loader } from '~shared/ui/loader';

import { Typography } from '@breezeventures/ui-kit/typography';
import {
  ResponsiveContainer,
  AreaChart,
  XAxis,
  YAxis,
  Tooltip,
  Area,
  CartesianGrid,
} from 'recharts';

import { renderTooltip } from './tooltip';

import { isMoneyType } from '../lib';
import type { SummaryDataField } from '../types';

type TotalAmountInfo = {
  [key in SummaryDataField]: string;
};

type Props = {
  filters: TransactionsSummaryFilter;
  dataField: SummaryDataField;
};

const DESKTOP_CHART_HEIGHT = 500;
const MOBILE_CHART_HEIGHT = 280;

export const SummaryGraph = ({ filters, dataField }: Props) => {
  const { data, isLoading, isFetching, isPlaceholderData } =
    useGetSummaryGraphQuery(filters);
  const isDesktop = useIsDesktop();
  const chartHeight = isDesktop ? DESKTOP_CHART_HEIGHT : MOBILE_CHART_HEIGHT;

  const totalAmount: TotalAmountInfo | undefined = useMemo(() => {
    if (data) {
      const total = data.reduce(
        (acc, cur) => ({
          loansAmount: acc.loansAmount + cur.loansAmount,
          salesAmount: acc.salesAmount + cur.salesAmount,
          loansCount: acc.loansCount + cur.loansCount,
        }),
        { loansAmount: 0, salesAmount: 0, loansCount: 0 }
      );
      return {
        loansAmount: formatMoney(total.loansAmount),
        salesAmount: formatMoney(total.salesAmount),
        loansCount: total.loansCount.toString(),
      };
    }
    return undefined;
  }, [data]);

  const formatDate = (value: string, type: 'short' | 'long') =>
    new Date(value).toLocaleDateString('en', {
      month: type,
      day: 'numeric',
    });

  if (isLoading) {
    return (
      <div className="relative flex flex-col" style={{ height: chartHeight }}>
        <Loader />
      </div>
    );
  }
  if (!data?.length) {
    return (
      <div
        className="flex justify-center pt-20 text-center"
        style={{ height: chartHeight }}
      >
        <Typography.Text size="m" color="secondary">
          We didn&rsquo;t find anything
        </Typography.Text>
      </div>
    );
  }

  return (
    <div
      className={`
        relative flex flex-col
        lg:min-h-[560px]
        [&_.recharts-cartesian-axis-line]:stroke-neutral-300
        [&_.recharts-cartesian-axis-tick-value]:fill-secondary
        [&_.recharts-cartesian-axis-tick-value]:typography-text-xs
      `}
    >
      {totalAmount && (
        <div>
          <Typography.Text size="s" tag="p" className="mb-2">
            {formatDate(filters.dateFrom, 'long')} —{' '}
            {formatDate(filters.dateTo, 'long')}
          </Typography.Text>
          <Typography.Title size="m" weight="bold">
            {totalAmount[dataField]}
          </Typography.Title>
        </div>
      )}
      {isFetching && isPlaceholderData && <Loader />}
      <ResponsiveContainer
        id="summary-graph-container"
        minWidth="100%"
        minHeight={chartHeight}
        height={chartHeight}
      >
        <AreaChart width={730} height={250} data={data} margin={{ top: 60 }}>
          <defs>
            <linearGradient id="dataColor" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#5BA6FD" stopOpacity={0.24} />
              <stop offset="95%" stopColor="#5BA6FD" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="date"
            minTickGap={isDesktop ? undefined : 32}
            interval={isDesktop ? undefined : 'preserveStartEnd'}
            tickFormatter={(v) => formatDate(v, 'short')}
          />
          <YAxis
            mirror
            type="number"
            axisLine={false}
            tickLine={false}
            tick={{
              transform: 'translate(0, -15)',
            }}
            tickFormatter={(v) => (isMoneyType(dataField) ? formatMoney(v) : v)}
          />

          <Tooltip content={renderTooltip} />
          <Area
            connectNulls
            type="monotone"
            dataKey={dataField}
            stroke="#5BA6FD"
            strokeWidth={2}
            fillOpacity={1}
            fill="url(#dataColor)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};
